/**
 * Fungsi pintar untuk mengekstrak dan memisahkan nama item dan kode secara otomatis,
 * mendukung format nama seperti:
 * - 'DEVIL\'S CLAW NR 7108' -> name: "DEVIL'S CLAW", code: "NR 7108"
 * - 'TABOO NEW 2420448' -> name: "TABOO NEW", code: "2420448"
 * - 'GOA 67.333 / LE MALE JEAN PAUL GAULTIER' -> name: "GOA / LE MALE JEAN PAUL GAULTIER", code: "67.333"
 * - 'LIBERTY 868394' -> name: "LIBERTY", code: "868394"
 * - 'Vanilla Milk (LZ-012)' -> name: "Vanilla Milk", code: "LZ-012"
 * - 'LZ-012 - Vanilla Milk' -> name: "Vanilla Milk", code: "LZ-012"
 * - 'Vanilla Milk - LZ-012' -> name: "Vanilla Milk", code: "LZ-012"
 */
export function extractNameAndCode(rawName, existingCode = '') {
  let name = (rawName || '').trim();
  let code = (existingCode || '').trim();

  // Jika kode sudah terisi, pastikan nama tidak duplikat dengan kode di ujungnya
  if (code) {
    return { name, code };
  }

  if (!name) {
    return { name: '', code: '' };
  }

  // 1. Cek kode di dalam tanda kurung: e.g. 'Item Name (LZ-012)'
  const parenMatch = name.match(/^(.*?)\s*\(([^)]*\d[^)]*)\)\s*$/);
  if (parenMatch) {
    return { name: parenMatch[1].trim(), code: parenMatch[2].trim() };
  }

  // 2. Cek pemisah strip ' - ': e.g. 'KODE - NAMA' atau 'NAMA - KODE'
  if (name.includes(' - ')) {
    const parts = name.split(' - ');
    if (parts.length === 2) {
      const p0 = parts[0].trim();
      const p1 = parts[1].trim();
      const isCode0 = /\d/.test(p0) && p0.length <= 15;
      const isCode1 = /\d/.test(p1) && p1.length <= 15;
      if (isCode0 && !isCode1) return { name: p1, code: p0 };
      if (isCode1 && !isCode0) return { name: p0, code: p1 };
      if (isCode0 && isCode1) {
        return p0.length < p1.length ? { name: p1, code: p0 } : { name: p0, code: p1 };
      }
    }
  }

  // 3. Cek kode tersemat sebelum garis miring: e.g. 'GOA 67.333 / LE MALE JEAN PAUL GAULTIER'
  const slashCodeMatch = name.match(/^([A-Za-z\s]+?)\s+(\d[\d.A-Za-z-]*)\s*(\/.*)$/);
  if (slashCodeMatch) {
    const cleanName = (slashCodeMatch[1] + ' ' + slashCodeMatch[3]).trim();
    return { name: cleanName, code: slashCodeMatch[2].trim() };
  }

  // 4. Cek kode di akhir string: e.g. 'LIBERTY 868394', 'TABOO NEW 2420448', "DEVIL'S CLAW NR 7108"
  const trailingCodeMatch = name.match(/^(.*?)\s+(([A-Za-z]{1,4}\s+)?\d[\d.A-Za-z\/-]*)$/);
  if (trailingCodeMatch && trailingCodeMatch[1].trim()) {
    let candidateName = trailingCodeMatch[1].trim();
    let candidateCode = trailingCodeMatch[2].trim();

    // Pastikan kata umum seperti 'NEW', 'OLD', 'MAN', dll tetap menjadi bagian nama
    const prefixWordMatch = candidateCode.match(/^([A-Za-z]{1,4})\s+(\d.*)$/);
    if (prefixWordMatch) {
      const prefix = prefixWordMatch[1].toUpperCase();
      const commonNameWords = new Set(['NEW', 'OLD', 'FOR', 'MAN', 'MEN', 'RED', 'TOP', 'BIG', 'PRO', 'MAX']);
      if (commonNameWords.has(prefix)) {
        candidateName = `${candidateName} ${prefixWordMatch[1]}`;
        candidateCode = prefixWordMatch[2];
      }
    }

    if (candidateCode.length <= 15) {
      return { name: candidateName, code: candidateCode };
    }
  }

  // 5. Cek kode di awal string: e.g. 'LZ-012 VANILLA MILK' atau 'NR-7108 DEVILS CLAW'
  const leadingCodeMatch = name.match(/^([A-Za-z]{0,4}\s*[-#]?\s*\d[\d.A-Za-z\/-]*)\s+(.*?)$/);
  if (leadingCodeMatch && leadingCodeMatch[2].trim()) {
    const candidateCode = leadingCodeMatch[1].trim();
    const candidateName = leadingCodeMatch[2].trim();
    // Jangan pisahkan satuan ukuran seperti 30ml, 50ml, dll
    if (candidateCode.length <= 12 && !/^(1|2|3|4|5|10|20|30|50|100|250|500|1000)\s*(ml|gr|kg|l)$/i.test(candidateCode)) {
      return { name: candidateName, code: candidateCode };
    }
  }

  return { name, code: '' };
}
